"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { canOptimizeImage } from "@/lib/images";
import { m, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  Calendar,
  ChevronDown,
  Mail,
  Star,
} from "lucide-react";
import type { PortfolioProject } from "@/lib/db/portfolio";
import { DescriptionBlocks } from "@/components/portfolio/description-blocks";
import { caseStudyPath } from "@/lib/portfolio-slug";

/**
 * Öffentliche Portfolio-Seite (Soft-Launch, nicht in der Navigation).
 *
 * Design-Idee „modern": großzügiges editorial Grid, Kategorie-Filter
 * als Pills, Cover-Bilder mit sanftem Zoom-Hover und Overlay,
 * Featured-Projekte mit Stern-Badge. Layout-Wechsel beim Filtern wird
 * mit framer-motion `layout` animiert – das gibt den „lebendigen"
 * Portfolio-Eindruck moderner Agentur-Sites ohne Masonry-Komplexität.
 */

const FILTERS = [
  { slug: "all", label: "Alle Projekte" },
  { slug: "web", label: "Websites" },
  { slug: "software", label: "Software & Apps" },
  { slug: "branding", label: "Design" },
  { slug: "marketing", label: "Marketing" },
  { slug: "video", label: "Video" },
  { slug: "other", label: "Sonstiges" },
] as const;

const CATEGORY_LABEL: Record<string, string> = {
  web: "Website",
  software: "Software",
  branding: "Design",
  marketing: "Marketing",
  video: "Video",
  other: "Projekt",
};

function ProjectCard({ project, priority = false }: { project: PortfolioProject; priority?: boolean }) {
  const [open, setOpen] = useState(false);
  const hasDetails = project.description.trim().length > 0;
  const host = project.project_url
    ? project.project_url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
    : null;

  const cover = (
    <div className="relative aspect-[16/10] overflow-hidden" style={{ backgroundColor: "var(--bg-tertiary)" }}>
      {canOptimizeImage(project.cover_image_path) ? (
        <Image
          src={project.cover_image_path}
          alt={project.title}
          fill
          priority={priority}
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
      ) : project.cover_image_path ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={project.cover_image_path}
          alt={project.title}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Briefcase className="h-10 w-10 opacity-20" style={{ color: "var(--text)" }} />
        </div>
      )}
      <div className="absolute left-3 top-3 flex gap-1.5">
        <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur-md" style={{ backgroundColor: "rgba(255,255,255,0.85)", color: "#101828" }}>
          {CATEGORY_LABEL[project.category] ?? "Projekt"}
        </span>
        {project.featured && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/90 px-2.5 py-1 text-[11px] font-semibold text-amber-950 backdrop-blur-md">
            <Star className="h-3 w-3 fill-current" />
            Highlight
          </span>
        )}
      </div>
      {project.project_url && (
        <div className="absolute inset-0 flex items-end justify-end bg-gradient-to-t from-black/40 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-gray-900">
            Website ansehen
            <ArrowUpRight className="h-3.5 w-3.5" />
          </span>
        </div>
      )}
    </div>
  );

  return (
    <article
      className="flex h-full flex-col overflow-hidden rounded-2xl border transition-all duration-200 hover:shadow-xl"
      style={{ backgroundColor: "var(--card-bg)", borderColor: "var(--border)", boxShadow: "var(--card-shadow)" }}
    >
      {project.project_url ? (
        <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="group block" aria-label={`${project.title} – Website ansehen`}>
          {cover}
        </a>
      ) : (
        <div className="group">{cover}</div>
      )}

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold leading-snug" style={{ color: "var(--text)" }}>
            <Link href={caseStudyPath(project)} className="hover:text-brand-500 hover:underline">
              {project.title}
            </Link>
          </h2>
        </div>
        {project.client_name && (
          <p className="mt-0.5 text-sm font-medium" style={{ color: "var(--text-brand)" }}>
            {project.client_name}
          </p>
        )}
        {(project.industry || project.location) && (
          <p className="mt-0.5 text-xs" style={{ color: "var(--text-tertiary)" }}>
            {[project.industry, project.location].filter(Boolean).join(" · ")}
          </p>
        )}
        {project.summary && (
          <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            {project.summary}
          </p>
        )}
        {project.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {project.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="rounded-full border px-2.5 py-0.5 text-[11px] font-medium" style={{ borderColor: "var(--border)", color: "var(--text-tertiary)" }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {hasDetails && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 hover:underline"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
              {open ? "Weniger anzeigen" : "Was wir gemacht haben"}
            </button>
            {open && (
              <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--border)" }}>
                <DescriptionBlocks text={project.description} />
              </div>
            )}
          </div>
        )}

        <Link href={caseStudyPath(project)} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 hover:underline">
          Zur Fallstudie
          <ArrowRight className="h-4 w-4" />
        </Link>
        {project.project_url && host && (
          <a
            href={project.project_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold transition-colors hover:text-brand-500"
            style={{ color: "var(--text-secondary)" }}
          >
            {host} ansehen
            <ArrowUpRight className="h-4 w-4" />
          </a>
        )}
      </div>
    </article>
  );
}

/* Empty-State: erscheint nur, solange kein Projekt freigeschaltet ist. */
function EmptyState() {
  return (
    <section className="flex min-h-[60vh] items-center py-20">
      <div className="mx-auto w-full max-w-2xl px-4 text-center lg:px-8">
        <div
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl"
          style={{
            backgroundColor: "var(--fi-brand-bg)",
            color: "var(--fi-brand-fg)",
          }}
        >
          <Briefcase className="h-8 w-8" />
        </div>
        <h1
          className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl"
          style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
        >
          Portfolio in Pflege
        </h1>
        <p
          className="mt-4 text-base leading-relaxed sm:text-lg"
          style={{ color: "var(--text-tertiary)" }}
        >
          Wir kuratieren gerade unsere aktuellen Kundenprojekte und stellen
          sie in Kürze hier ein. Bei Interesse an konkreten Referenzen aus
          Ihrer Branche schreiben Sie uns gerne direkt.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            href="/beratung"
            className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Kostenloses Erstgespräch
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href="mailto:kontakt@dmd-studio.de"
            className="inline-flex items-center gap-2 rounded-lg border px-5 py-3 text-base font-semibold transition-colors"
            style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
          >
            <Mail className="h-5 w-5" />
            Referenzen anfragen
          </a>
        </div>
      </div>
    </section>
  );
}

export function PortfolioClient({ projects }: { projects: PortfolioProject[] }) {
  const [filter, setFilter] = useState<string>("all");

  // Nur Filter mit Inhalt anbieten – leere Kategorien verstecken.
  const availableFilters = useMemo(
    () =>
      FILTERS.filter(
        (f) =>
          f.slug === "all" || projects.some((p) => p.category === f.slug),
      ),
    [projects],
  );

  const filtered = useMemo(
    () =>
      filter === "all"
        ? projects
        : projects.filter((p) => p.category === filter),
    [projects, filter],
  );

  if (projects.length === 0) return <EmptyState />;

  return (
    <>
      {/* Hero */}
      <section className="py-12 lg:py-20" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold" style={{ color: "var(--text-brand)" }}>
              Portfolio
            </p>
            <h1
              className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl"
              style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
            >
              Arbeiten, die{" "}
              <span className="bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">
                wirken
              </span>
            </h1>
            <p className="mt-5 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              Ausgewählte Projekte aus Web, Software, Design und Marketing —
              umgesetzt für Mittelstand und Handwerk. Jedes Projekt mit klarem
              Ziel und messbarem Ergebnis.
            </p>
          </div>

          {/* Filter-Pills */}
          {availableFilters.length > 2 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {availableFilters.map((f) => {
                const active = filter === f.slug;
                return (
                  <button
                    key={f.slug}
                    type="button"
                    onClick={() => setFilter(f.slug)}
                    className="rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-150"
                    style={
                      active
                        ? {
                            backgroundColor: "var(--fi-brand-bg)",
                            borderColor: "var(--fi-brand-fg)",
                            color: "var(--fi-brand-fg)",
                          }
                        : {
                            borderColor: "var(--border)",
                            color: "var(--text-secondary)",
                            backgroundColor: "var(--card-bg)",
                          }
                    }
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="pb-16 lg:pb-24" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <m.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* initial={false}: beim ersten Rendern keine Einblendung – die Karten liegen im ersten Viewport (LCP) */}
            <AnimatePresence mode="popLayout" initial={false}>
              {filtered.map((project, index) => (
                <m.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Die ersten beiden Karten sind das LCP-Element – vorrangig laden */}
                  <ProjectCard project={project} priority={index < 2} />
                </m.div>
              ))}
            </AnimatePresence>
          </m.div>

          {filtered.length === 0 && (
            <p
              className="py-16 text-center text-sm"
              style={{ color: "var(--text-tertiary)" }}
            >
              In dieser Kategorie sind aktuell keine Projekte freigeschaltet.
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 lg:py-20" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
          <h2
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
            style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
          >
            Ihr Projekt könnte das nächste sein
          </h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            Erzählen Sie uns im kostenlosen Erstgespräch, was Sie vorhaben —
            wir zeigen Ihnen, wie wir es umsetzen würden.
          </p>
          <Link
            href="/beratung"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-3 text-base font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md"
          >
            <Calendar className="h-5 w-5" />
            Kostenloses Erstgespräch buchen
          </Link>
        </div>
      </section>
    </>
  );
}
